import { useState } from 'react'

import { CallButton } from '@/components/Aircall/CallButton'
import { SmsButton } from '@/components/Aircall/SmsButton'
import { type AircallNumber } from '@/components/Aircall/types'
import { GoogleOauth } from '@/components/GoogleOauth'
import { type InsuranceCarrier } from '@/models/insuranceCarrier'
import { type SalesLead } from '@/models/salesLead'
import { normalizeE164 } from '@/utils/phone'
import { CarrierEmailComposer } from './CarrierEmailComposer'
import { CarrierSmsComposer } from './CarrierSmsComposer'
import { type LeadEmail } from './contactEmails'
import { EmailButton } from './EmailButton'

interface CarrierComposerProps {
  lead: SalesLead
  carrier: InsuranceCarrier
}

// Each carrier contact becomes a dropdown entry labeled by name, carrying its
// contactId so the send/call is attributed to that contact.
function carrierEmails(carrier: InsuranceCarrier): LeadEmail[] {
  return carrier.contacts
    .filter((c) => c.email)
    .map((c) => ({ label: c.name, value: c.email, contactId: c.id }))
}

function carrierNumbers(carrier: InsuranceCarrier): AircallNumber[] {
  const numbers: AircallNumber[] = []
  for (const c of carrier.contacts) {
    const e164 = normalizeE164(c.phone)
    if (e164) numbers.push({ label: c.name, value: e164, contactId: c.id })
  }
  return numbers
}

export function CarrierComposer({ lead, carrier }: CarrierComposerProps) {
  const [mode, setMode] = useState<'idle' | 'email' | 'sms'>('idle')
  const [contactId, setContactId] = useState('')

  if (carrier.contacts.length === 0) {
    return <p className="text-muted-foreground text-sm">Add a contact to this carrier to message them.</p>
  }

  const emails = carrierEmails(carrier)
  const numbers = carrierNumbers(carrier)

  return (
    <div className="flex flex-col gap-3">
      {mode === 'idle' && (
        <div className="flex flex-wrap gap-2">
          {emails.length > 0 && (
            <GoogleOauth connectLabel="Connect Google to email">
              <EmailButton
                emails={emails}
                enableAiDraft={false}
                onCompose={(recipient) => {
                  setContactId(recipient?.contactId ?? '')
                  setMode('email')
                }}
              />
            </GoogleOauth>
          )}
          <CallButton leadId={lead.id} numbers={numbers} />
          <SmsButton
            numbers={numbers}
            onCompose={(recipient) => {
              setContactId(recipient.contactId ?? '')
              setMode('sms')
            }}
          />
        </div>
      )}

      {mode === 'email' && (
        <CarrierEmailComposer
          lead={lead}
          carrier={carrier}
          initialContactId={contactId}
          onSent={() => setMode('idle')}
          onCancel={() => setMode('idle')}
        />
      )}

      {mode === 'sms' && (
        <CarrierSmsComposer
          lead={lead}
          carrier={carrier}
          initialContactId={contactId}
          onSent={() => setMode('idle')}
          onCancel={() => setMode('idle')}
        />
      )}
    </div>
  )
}
